import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Button, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import BackupIcon from '@mui/icons-material/Backup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';
import config from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import moment from 'moment-timezone';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CorporateManagement = ({ showMessage }) => {
  const [loading, setLoading] = useState(false);
  const [corporateUsers, setCorporateUsers] = useState([]);
  const [prefectures, setPrefectures] = useState([]);
  const [taggedUserStats, setTaggedUserStats] = useState([]);
  const [monthlyTagStats, setMonthlyTagStats] = useState([]);
  const [adUsageStats, setAdUsageStats] = useState({
    top_ad_clicks: 0,
    inline_ad_clicks: 0,
    current_month_amount: 0,
    next_month_amount: 0,
    current_month_period: '',
    next_month_period: ''
  });
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  
  // タブ関連の状態
  const [tabValue, setTabValue] = useState(0);
  
  // フィルター関連の状態
  const [prefectureFilter, setPrefectureFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // ページネーション関連の状態
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [corporateToDelete, setCorporateToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  // CSVアップロード関連の状態
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadStatus, setCsvUploadStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [csvUploadError, setCsvUploadError] = useState(null);

  // 都道府県別料金設定関連の状態
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [selectedPrefecture, setSelectedPrefecture] = useState(null);
  const [prefecturePricing, setPrefecturePricing] = useState({
    corporate_price: 11000,
    corporate_discount: 0,
    user_price: 1100,
    user_discount: 0
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingConfirmDialogOpen, setPricingConfirmDialogOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');

  // 収益表示用の状態を追加
  const [revenueData, setRevenueData] = useState({
    corporate_revenue: 0,
    user_revenue: 0,
    ad_revenue: 0,
    total_revenue: 0,
    corporate_count: 0,
    total_tagged_users: 0
  });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);

  useEffect(() => {
    fetchPrefectures();
    // 利用可能な月のリストを生成
    generateAvailableMonths();
    // コンポーネントマウント時に確実にデータを取得
    setTotalCount(0); // 初期値を設定
    fetchCorporateUsers();
  }, []);

  // 都道府県選択時に料金設定を取得
  useEffect(() => {
    if (selectedPrefecture) {
      fetchPrefecturePricing(selectedPrefecture.code);
    }
  }, [selectedPrefecture]);

  const fetchPrefectures = async () => {
    try {
      
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/prefectures`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // レスポンスデータの構造を確認するためのデバッグログ
      
      
      // APIのレスポンス形式に合わせて修正
      if (Array.isArray(response.data)) {
        
        setPrefectures(response.data);
      } else if (response.data && response.data.prefectures) {
        
        setPrefectures(response.data.prefectures);
      } else {
        
        setPrefectures([]);
      }
    } catch (error) {
      
      showMessage('都道府県データの取得に失敗しました', 'error');
    }
  };

  // 都道府県別料金設定を取得
  const fetchPrefecturePricing = async (prefectureCode) => {
    setPricingLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/admin/prefecture-pricing/${prefectureCode}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (response.data.status === 'success') {
        setPrefecturePricing(response.data.data);
        
        // 次月の21日を計算（適用開始日）
        const now = new Date();
        let nextMonth = now.getMonth() + 2; // JavaScriptの月は0始まりなので+2
        let nextYear = now.getFullYear();
        
        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear += 1;
        }
        
        setEffectiveDate(`${nextYear}年${nextMonth}月21日`);
      } else {
        showMessage(response.data.message || '料金設定の取得に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('料金設定の取得に失敗しました', 'error');
    } finally {
      setPricingLoading(false);
    }
  };

  // 都道府県別料金設定を更新
  const updatePrefecturePricing = async () => {
    setPricingLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/stats/admin/prefecture-pricing/${selectedPrefecture.code}`,
        prefecturePricing,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );
      
      if (response.data.status === 'success') {
        showMessage('料金設定が正常に更新されました', 'success');
        setPricingConfirmDialogOpen(false);
        setPricingDialogOpen(false);
      } else {
        showMessage(response.data.message || '料金設定の更新に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('料金設定の更新に失敗しました', 'error');
    } finally {
      setPricingLoading(false);
    }
  };

  // 料金設定ダイアログを開く
  const handleOpenPricingDialog = (prefecture) => {
    setSelectedPrefecture(prefecture);
    setPricingDialogOpen(true);
  };

  // 料金設定ダイアログを閉じる
  const handleClosePricingDialog = () => {
    setPricingDialogOpen(false);
    setSelectedPrefecture(null);
  };

  // 料金設定確認ダイアログを開く
  const handleOpenConfirmDialog = () => {
    setPricingConfirmDialogOpen(true);
  };

  // 料金設定確認ダイアログを閉じる
  const handleCloseConfirmDialog = () => {
    setPricingConfirmDialogOpen(false);
  };

  // 料金設定の入力値を更新
  const handlePricingChange = (event) => {
    const { name, value } = event.target;
    setPrefecturePricing({
      ...prefecturePricing,
      [name]: parseInt(value, 10) || 0
    });
  };

  const fetchCorporateUsers = async () => {
    setLoading(true);
    try {
      // デバッグ出力を追加
      
      
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        params: {
          prefecture: prefectureFilter,
          search: searchQuery,
          page: page + 1,
          per_page: rowsPerPage
        }
      });
      
      // レスポンスデータをデバッグ出力
      
      
      if (response.data.status === 'success') {
        setCorporateUsers(response.data.data.corporate_users || []);
        setTotalCount(response.data.data.pagination.total || 0);
      } else {
        showMessage(response.data.message || 'データの取得に失敗しました', 'error');
        setCorporateUsers([]);
        setTotalCount(0);
      }
    } catch (error) {
      
      showMessage('企業ユーザーデータの取得に失敗しました', 'error');
      setCorporateUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCorporateDetail = async (corporateId) => {
    try {
      setLoading(true);
      
      // 企業ユーザー詳細データの取得
              const detailResponse = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/${corporateId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (detailResponse.data.status === 'success') {
        setSelectedCorporate(detailResponse.data.data.corporate);
        setTaggedUserStats(detailResponse.data.data.tagged_users || []);
        setMonthlyTagStats(detailResponse.data.data.monthly_stats || []);
        
        // 広告利用データの基本情報を取得
        const adUsage = detailResponse.data.data.ad_usage || {};
        
        // 日付情報を計算
        const { currentPeriod, nextPeriod } = calculateAdPeriods();
        
        // まず既存のAPIレスポンスからデータをチェック
        if (adUsage && (adUsage.top_ad_clicks !== undefined || adUsage.current_month_amount !== undefined)) {
          // 既存のレスポンスにデータがある場合はそれを使用
          setAdUsageStats({
            ...adUsage,
            current_month_period: currentPeriod,
            next_month_period: nextPeriod
          });
          setDetailDialogOpen(true);
          return;
        }
        
        // 広告の利用料とクリック回数を取得（個別エンドポイント）
        try {
          // 広告利用料を取得
          const billingResponse = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/${corporateId}/ads/billing`, {
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Cache-Control': 'no-cache'
            }
          });
          
          // クリック回数を取得
                      const clicksResponse = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/${corporateId}/ads/clicks`, {
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Cache-Control': 'no-cache'
            }
          });
          
          // レスポンスが成功の場合、データを統合
          if (billingResponse.data.status === 'success' && clicksResponse.data.status === 'success') {
            // 広告利用データを拡張
            const curM = billingResponse.data.current_month || {};
            const nxtM = billingResponse.data.next_month || {};
            setAdUsageStats({
              ...adUsage,
              top_ad_clicks: clicksResponse.data.top_ad_clicks || 0,
              inline_ad_clicks: clicksResponse.data.inline_ad_clicks || 0,
              current_month_amount: curM.amount || 0,
              next_month_amount: nxtM.amount || 0,
              // 内訳（基本料金・広告費・単価・タグ付け数）
              current_base_amount: curM.base_amount || 0,
              current_ad_amount: curM.ad_amount || 0,
              current_tagged_count: curM.tagged_count || 0,
              corporate_price: curM.corporate_price || 0,
              user_price: curM.user_price || 0,
              next_base_amount: nxtM.base_amount || 0,
              next_ad_amount: nxtM.ad_amount || 0,
              next_tagged_count: nxtM.tagged_count || 0,
              current_month_period: currentPeriod,
              next_month_period: nextPeriod
            });
          } else {
            // エラーがある場合は基本情報のみ設定
            setAdUsageStats({
              ...adUsage,
              current_month_period: currentPeriod,
              next_month_period: nextPeriod
            });
          }
        } catch (error) {
          
          
          // 個別エンドポイント取得に失敗した場合は、代替方法でデータを取得
          try {
            // 企業ユーザーの広告データを取得する代替方法
            const corporateAdsResponse = await axios.get(`${config.API_BASE_URL}/api/admin/corporate/${corporateId}/ads`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
            });
            
            if (corporateAdsResponse.data.status === 'success') {
              const adsData = corporateAdsResponse.data.data || {};
              setAdUsageStats({
                ...adUsage,
                top_ad_clicks: adsData.total_top_clicks || 0,
                inline_ad_clicks: adsData.total_infeed_clicks || 0,
                current_month_amount: adsData.current_month_amount || 0,
                next_month_amount: adsData.next_month_amount || 0,
                current_month_period: currentPeriod,
                next_month_period: nextPeriod
              });
            } else {
              // 基本情報のみ設定
              setAdUsageStats({
                ...adUsage,
                current_month_period: currentPeriod,
                next_month_period: nextPeriod
              });
            }
          } catch (backupError) {
            
            // 最終的に基本情報のみ設定
            setAdUsageStats({
              ...adUsage,
              current_month_period: currentPeriod,
              next_month_period: nextPeriod
            });
          }
        }
        
        setDetailDialogOpen(true);
      } else {
        showMessage(detailResponse.data.message || '詳細データの取得に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('企業詳細データの取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 広告期間を計算する関数
  const calculateAdPeriods = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();
    
    let currentStartDate, currentEndDate, nextStartDate, nextEndDate;
    
    // 現在日付が21日以降なら、当月は「前月21日～当月20日」
    if (currentDay >= 21) {
      // 当月期間（前月21日～当月20日）
      currentStartDate = new Date(currentYear, currentMonth - 1, 21);
      currentEndDate = new Date(currentYear, currentMonth, 20);
      
      // 次月期間（当月21日～翌月20日）
      nextStartDate = new Date(currentYear, currentMonth, 21);
      nextEndDate = new Date(currentYear, currentMonth + 1, 20);
    } 
    // 現在日付が20日以前なら、当月は「前々月21日～前月20日」
    else {
      // 当月期間（前々月21日～前月20日）
      currentStartDate = new Date(currentYear, currentMonth - 2, 21);
      currentEndDate = new Date(currentYear, currentMonth - 1, 20);
      
      // 次月期間（前月21日～当月20日）
      nextStartDate = new Date(currentYear, currentMonth - 1, 21);
      nextEndDate = new Date(currentYear, currentMonth, 20);
    }
    
    // 期間を「YYYY年MM月DD日〜YYYY年MM月DD日」形式でフォーマット
    const formatDateRange = (start, end) => {
      return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日〜${end.getFullYear()}年${end.getMonth() + 1}月${end.getDate()}日`;
    };
    
    return {
      currentPeriod: formatDateRange(currentStartDate, currentEndDate),
      nextPeriod: formatDateRange(nextStartDate, nextEndDate)
    };
  };

  const downloadCsv = async (corporateId) => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/${corporateId}/csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        responseType: 'blob'
      });
      
      // CSVファイルをダウンロード
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const corporateUser = corporateUsers.find(user => user.id === corporateId);
      const fileName = `corporate_stats_${corporateUser?.username || corporateId}_${moment().format('YYYYMMDD')}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage('CSVファイルのダウンロードが完了しました', 'success');
    } catch (error) {
      
      showMessage('CSVファイルのダウンロードに失敗しました', 'error');
    }
  };

  // corporate_links の異常データをクリーンアップする。
  // まずドライランで対象件数を確認し、確認ダイアログ後に実削除する。
  const handleCleanupLinks = async () => {
    try {
      // 1) ドライランで対象を取得
      const dryRes = await axios.post(
        `${config.API_BASE_URL}/api/admin/cleanup-corporate-links`,
        { dry_run: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' } }
      );
      const targets = dryRes.data?.targets || [];
      const count = dryRes.data?.target_count || 0;
      if (count === 0) {
        showMessage('クリーンアップ対象の異常データはありませんでした', 'info');
        return;
      }
      // 理由の内訳を作成
      const reasons = {};
      targets.forEach(t => { reasons[t.reason] = (reasons[t.reason] || 0) + 1; });
      const detail = Object.entries(reasons).map(([r, c]) => `・${r}: ${c}件`).join('\n');
      if (!window.confirm(`異常なタグ付けデータ ${count} 件を削除します。\n\n${detail}\n\nこの操作は元に戻せません。実行しますか？`)) {
        return;
      }
      // 2) 実削除
      const delRes = await axios.post(
        `${config.API_BASE_URL}/api/admin/cleanup-corporate-links`,
        { dry_run: false },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' } }
      );
      const deleted = delRes.data?.deleted_count || 0;
      showMessage(`異常データ ${deleted} 件を削除しました`, 'success');
      // 一覧を再取得
      fetchCorporateUsers();
    } catch (error) {
      console.error('クリーンアップエラー:', error);
      showMessage(error.response?.data?.message || 'クリーンアップに失敗しました', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // ページ変更後にデータを再取得するよう変更
    setTimeout(() => {
      fetchCorporateUsers();
    }, 0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // 表示件数変更後にデータを再取得するよう変更
    setTimeout(() => {
      fetchCorporateUsers();
    }, 0);
  };

  const handlePrefectureFilterChange = (event) => {
    const newValue = event.target.value;
    setPrefectureFilter(newValue);
  };

  const handleSearchQueryChange = (event) => {
    const newValue = event.target.value;
    
    setSearchQuery(newValue);
  };

  const handleApplyFilters = () => {
    setPage(0);
    // 企業ユーザー一覧と収益データを同時に更新
    fetchCorporateUsers();
    fetchRevenueData(selectedMonth);
  };

  const handleDetailClose = () => {
    setDetailDialogOpen(false);
  };

  // 削除ダイアログを開く処理
  const handleOpenDeleteDialog = (corporate) => {
    setCorporateToDelete(corporate);
    setDeleteDialogOpen(true);
  };

  // 削除ダイアログを閉じる処理
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCorporateToDelete(null);
  };

  // 企業ユーザー削除処理
  const handleDeleteCorporate = async () => {
    if (!corporateToDelete) return;
    
    setDeleteInProgress(true);
    try {
      const response = await axios.delete(
        `${config.API_BASE_URL}/api/stats/corporate/${corporateToDelete.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      
      if (response.data.status === 'success') {
        showMessage(`${corporateToDelete.shop_name}が削除されました。タグ付けユーザー${response.data.deleted_tagged_count}件も削除されました`, 'success');
        // 企業ユーザー一覧を再取得
        fetchCorporateUsers();
      } else {
        showMessage(response.data.message || '削除処理に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('企業ユーザーの削除に失敗しました', 'error');
    } finally {
      setDeleteInProgress(false);
      handleCloseDeleteDialog();
    }
  };

  // バックアップCSVダウンロード処理
  const downloadBackupCsv = async (corporateId) => {
    if (!corporateId) return;
    
    setBackupLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/corporate/${corporateId}/backup-csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // CSVファイルをダウンロード
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const corporateUser = corporateToDelete;
      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const fileName = `backup_corporate_${corporateUser?.username || corporateId}_${timestamp}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage('バックアップCSVファイルのダウンロードが完了しました', 'success');
    } catch (error) {
      
      showMessage('バックアップCSVファイルのダウンロードに失敗しました', 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  // CSVアップロードダイアログを開く
  const handleOpenCsvUploadDialog = () => {
    setCsvFile(null);
    setCsvUploadStatus('idle');
    setCsvUploadOpen(true);
  };

  // CSVアップロードダイアログを閉じる
  const handleCloseCsvUploadDialog = () => {
    setCsvUploadOpen(false);
    setCsvFile(null);
    setCsvUploadStatus('idle');
    setCsvUploadError(null);
  };

  // CSVファイルを選択
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      showMessage('CSVファイル形式のみをアップロードしてください', 'error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setCsvFile(file);
  };

  // CSVファイルをアップロード
  const handleUploadCsv = async () => {
    if (!csvFile) {
      showMessage('ファイルを選択してください', 'error');
      return;
    }
    
    setCsvUploadStatus('loading');
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(
        `${config.API_BASE_URL}/api/users/import/corporate-csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.status === 'success') {
        setCsvUploadStatus('success');
        showMessage('CSVファイルのインポートに成功しました', 'success');
        // 企業ユーザー一覧を再取得
        fetchCorporateUsers();
      } else {
        setCsvUploadStatus('error');
        setCsvUploadError(response.data.message || 'CSVファイルのインポートに失敗しました');
        showMessage('CSVファイルのインポートに失敗しました', 'error');
      }
    } catch (error) {
      setCsvUploadStatus('error');
      setCsvUploadError(error.response?.data?.message || error.message || 'CSVファイルのインポートに失敗しました');
      
      showMessage('CSVファイルのインポートに失敗しました', 'error');
    } finally {
      // インポート完了後もダイアログは閉じない（結果を表示するため）
    }
  };

  // 利用可能な月のリストを生成し、現在の日付に基づいてデフォルト月を設定
  const generateAvailableMonths = () => {
    const months = [];
    const now = moment();
    
    // 過去6ヶ月、現在の月、将来2ヶ月を含む選択肢を生成（計9ヶ月）
    for (let i = 6; i >= -2; i--) {
      const monthMoment = moment().subtract(i, 'months');
      months.push({
        value: monthMoment.format('YYYYMM'),
        label: monthMoment.format('YYYY年M月')
      });
    }
    
    setAvailableMonths(months);
    
    // 現在の日が何日か確認
    const currentDay = now.date();
    
    // 現在の月を取得
    let defaultMonth = now.format('YYYYMM');
    
    // 常に当月をデフォルトに設定
    
    
    setSelectedMonth(defaultMonth);
    
    // 選択された月のデータを取得（更新が確実に完了してから実行するため、少し遅延）
    setTimeout(() => {
      
      if (defaultMonth) {
        fetchRevenueData(defaultMonth);
      } else {
        
        // 現在の月を再取得して強制的に設定
        const currentMonth = moment().format('YYYYMM');
        
        fetchRevenueData(currentMonth);
      }
    }, 100);
  };

  // 収益データを取得する関数
  const fetchRevenueData = async (monthParam = selectedMonth) => {
    try {
      setRevenueLoading(true);
      
      // 選択された年月から集計期間の開始日と終了日を計算
      const yearMonth = monthParam || selectedMonth;
      
      
      
      // API呼び出し用のパラメータをログ出力
      
      
      // API呼び出し用のパラメータに変換
      // yearMonthをAPIに渡す
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/admin/revenue`, {
          params: {
            yearMonth: yearMonth,
            prefecture: prefectureFilter  // undefinedを削除し、企業ユーザー一覧と同じ形式に
          },
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      
      
      
      if (response.data.status === 'success') {
        setRevenueData(response.data.data);
      } else {
        
        showMessage('収益データの取得に失敗しました', 'error');
      }
    } catch (error) {
      
      
      showMessage('収益データの取得中にエラーが発生しました', 'error');
    } finally {
      setRevenueLoading(false);
    }
  };

  // 月選択ハンドラ
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    
    setSelectedMonth(newMonth);
    
    // 期間変更後にデータを再取得（値の設定が確実に完了してから実行）
    setTimeout(() => {
      
      if (newMonth) {
        fetchRevenueData(newMonth);
      } else {
        
      }
    }, 100);
  };

  // 収益表示用コンポーネント
  const RevenueDisplay = () => {
    // 選択された月の集計期間を表示するための関数
    const getMonthPeriodText = () => {
      if (!selectedMonth) return '';
      
      // 選択された年月からモーメントオブジェクトを作成
      const yearStr = selectedMonth.substring(0, 4);
      const monthStr = selectedMonth.substring(4, 6);
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);
      
      // 前月を計算
      let prevMonth = month - 1;
      let prevYear = year;
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear -= 1;
      }
      
      // 集計期間の表示形式：前月21日～当月20日
      return `${prevYear}年${prevMonth}月21日～${year}年${month}月20日`;
    };

    // 現在より未来の月かどうかをチェック
    const isFutureMonth = () => {
      if (!selectedMonth) return false;
      
      const now = moment();
      const currentYearMonth = parseInt(now.format('YYYYMM'));
      const selectedYearMonth = parseInt(selectedMonth);
      
      return selectedYearMonth > currentYearMonth;
    };

    // 収益データがAPIからの将来予測データかどうか
    const isFutureData = revenueData.is_future === true;
    
    // 未来の月の場合の表示スタイル
    const futureStyle = isFutureMonth() ? { 
      backgroundColor: '#f5f9fa', 
      border: '1px dashed #b0bec5',
      position: 'relative'
    } : {};

    // CSVをダウンロードする関数
    const handleDownloadCsv = () => {
      downloadRevenueCsv();
    };

    return (
      <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#f5f5f5', ...futureStyle }}>
        {isFutureMonth() && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 10, 
              right: 10, 
              bgcolor: 'info.light', 
              color: 'info.contrastText',
              px: 1, 
              py: 0.5, 
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          >
            予測データ
          </Box>
        )}
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="month-select-label">表示月</InputLabel>
                <Select
                  labelId="month-select-label"
                  id="month-select"
                  value={selectedMonth}
                  label="表示月"
                  onChange={handleMonthChange}
                >
                  {availableMonths.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                      {parseInt(month.value) > parseInt(moment().format('YYYYMM')) && ' (予測)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={10} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                収益計算期間: {getMonthPeriodText()}
                {prefectureFilter && prefectures.find(p => p.code === prefectureFilter) ? 
                  ` - ${prefectures.find(p => p.code === prefectureFilter).name}` : ''}
                {isFutureMonth() && ' (予測データ)'}
              </Typography>
            </Grid>
            <Grid item xs={2} sm={2} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCsv}
                disabled={revenueLoading}
              >
                CSV
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', backgroundColor: '#e3f2fd' }}>
                <Typography variant="subtitle2" color="textSecondary">企業ユーザー収益</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {revenueLoading ? <CircularProgress size={20} /> : 
                    `¥${revenueData.corporate_revenue.toLocaleString()}`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  企業数: {revenueData.corporate_count}社
                  {isFutureMonth() && ' (予測)'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', backgroundColor: '#e8f5e9' }}>
                <Typography variant="subtitle2" color="textSecondary">投稿ユーザー収益</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {revenueLoading ? <CircularProgress size={20} /> : 
                    `¥${revenueData.user_revenue.toLocaleString()}`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  ユーザー数: {revenueData.total_tagged_users}人
                  {isFutureMonth() && ' (予測)'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', backgroundColor: '#fff8e1' }}>
                <Typography variant="subtitle2" color="textSecondary">広告収入</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {revenueLoading ? <CircularProgress size={20} /> : 
                    `¥${revenueData.ad_revenue.toLocaleString()}`}
                </Typography>
                {isFutureMonth() && (
                  <Typography variant="caption" color="textSecondary">
                    予測データ
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', backgroundColor: '#fbe9e7' }}>
                <Typography variant="subtitle2" color="textSecondary">トータル収益</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {revenueLoading ? <CircularProgress size={20} /> : 
                    `¥${revenueData.total_revenue.toLocaleString()}`}
                </Typography>
                {isFutureMonth() && (
                  <Typography variant="caption" color="textSecondary">
                    予測データ
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // 収益CSVダウンロード関数
  const downloadRevenueCsv = async () => {
    try {
      // ボタンをクリックした時点の選択月とフィルターを使用
      const params = {
        yearMonth: selectedMonth
      };
      
      // 都道府県フィルターが選択されている場合はパラメータに追加
      if (prefectureFilter) {
        params.prefecture = prefectureFilter;
      }
      
      // ダウンロード中メッセージを表示
      showMessage('CSVファイルのダウンロードを開始します...', 'info');
      
      // APIリクエストを発行
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/admin/revenue/csv`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          responseType: 'blob' // 重要: BLOBとして受信
        }
      );
      
      // BlobからURLを作成
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // ダウンロードリンクを作成
      const link = document.createElement('a');
      link.href = url;
      
      // ファイル名を設定
      const fileName = `revenue_report_${selectedMonth}_${prefectureFilter || 'all'}.csv`;
      link.setAttribute('download', fileName);
      
      // リンクをクリックしてダウンロード
      document.body.appendChild(link);
      link.click();
      
      // 使用後にリンクとURLを解放
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showMessage('CSVファイルのダウンロードが完了しました', 'success');
    } catch (error) {
      
      showMessage('収益データのCSVダウンロードに失敗しました', 'error');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* タブナビゲーション */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="corporate management tabs">
          <Tab label="企業ユーザー一覧" id="tab-0" />
          <Tab label="ユーザー管理" id="tab-1" />
          <Tab label="統計情報" id="tab-2" />
        </Tabs>
      </Box>

      {/* 企業ユーザー一覧タブ */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="prefecture-filter-label">都道府県</InputLabel>
                <Select
                  labelId="prefecture-filter-label"
                  id="prefecture-filter"
                  value={prefectureFilter}
                  label="都道府県"
                  onChange={handlePrefectureFilterChange}
                >
                  <MenuItem value="">全て</MenuItem>
                  {prefectures.map((prefecture) => (
                    <MenuItem key={prefecture.code} value={prefecture.code}>
                      {prefecture.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="search-query"
                label="検索"
                size="small"
                value={searchQuery}
                onChange={handleSearchQueryChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={6} sm={2}>
              <Button 
                fullWidth 
                variant="contained" 
                color="primary"
                onClick={handleApplyFilters}
              >
                検索
              </Button>
            </Grid>
            
            <Grid item xs={6} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setPrefectureFilter('');
                  setSearchQuery('');
                  setPage(0);
                  fetchCorporateUsers();
                }}
              >
                リセット
              </Button>
            </Grid>
          </Grid>
          
          {/* 都道府県別料金設定ボタン */}
          {prefectureFilter && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  const selectedPref = prefectures.find(p => p.code === prefectureFilter);
                  if (selectedPref) {
                    handleOpenPricingDialog(selectedPref);
                  }
                }}
              >
                {prefectures.find(p => p.code === prefectureFilter)?.name || '都道府県'}の料金設定
              </Button>
            </Box>
          )}
        </Paper>

        {/* データクリーンアップ: corporate_links の異常データを削除 */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleCleanupLinks}
          >
            異常なタグ付けデータを整理
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>企業名</TableCell>
                <TableCell>ユーザー名</TableCell>
                <TableCell>都道府県</TableCell>
                <TableCell align="center">タグ付け数</TableCell>
                <TableCell align="center">登録日</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : corporateUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    企業ユーザーが見つかりません
                  </TableCell>
                </TableRow>
              ) : (
                corporateUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.shop_name}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.prefecture_name}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={user.tagged_user_count} 
                        size="small" 
                        color={user.tagged_user_count > 0 ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {moment(user.created_at).format('YYYY/MM/DD')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="詳細を表示">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => fetchCorporateDetail(user.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="CSVをダウンロード">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => downloadCsv(user.id)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="バックアップCSV">
                        <IconButton 
                          size="small" 
                          color="secondary"
                          onClick={() => downloadBackupCsv(user.id)}
                        >
                          <BackupIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleOpenDeleteDialog(user)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={
              ({from, to, count}) => `${from}～${to} / ${count !== -1 ? count : `${to}以上`}`
            }
          />
        </TableContainer>
      </TabPanel>

      {/* ユーザー管理タブ */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>ユーザー管理</Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    CSVファイルからインポート
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    バックアップCSVファイルから企業ユーザーとタグ付けユーザーを一括登録・復元できます。
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadFileIcon />}
                    onClick={handleOpenCsvUploadDialog}
                    fullWidth
                  >
                    CSVファイルをアップロード
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* 統計情報タブ */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>統計概要</Typography>
              <Typography variant="body2">
                企業ユーザー別の詳細統計情報は、企業ユーザー一覧から各企業の詳細ボタンを押して確認できます。
                毎月20日23:59に自動でCSVファイルが生成され、各企業ユーザー別にダウンロード可能になります。
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 収益表示コンポーネントを追加 */}
      <RevenueDisplay />

      {/* 企業詳細ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCorporate?.shop_name || '企業詳細'}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* 基本情報 */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>基本情報</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">企業名</Typography>
                      <Typography variant="body1">{selectedCorporate?.shop_name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">ユーザー名</Typography>
                      <Typography variant="body1">{selectedCorporate?.username || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">都道府県</Typography>
                      <Typography variant="body1">{selectedCorporate?.prefecture_name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">登録日</Typography>
                      <Typography variant="body1">
                        {selectedCorporate?.created_at ? moment(selectedCorporate.created_at).format('YYYY/MM/DD') : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* タグ付けユーザー情報 */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>タグ付けユーザー情報</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ユーザー名</TableCell>
                          <TableCell>タグ名</TableCell>
                          <TableCell>作成日</TableCell>
                          <TableCell>削除日</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {taggedUserStats.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              タグ付けユーザーが見つかりません
                            </TableCell>
                          </TableRow>
                        ) : (
                          taggedUserStats.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.username}</TableCell>
                              <TableCell>{user.tag_name}</TableCell>
                              <TableCell>
                                {user.created_at ? moment(user.created_at).format('YYYY/MM/DD') : '-'}
                              </TableCell>
                              <TableCell>
                                {user.deleted_at ? moment(user.deleted_at).format('YYYY/MM/DD') : '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* 月次統計情報 */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>月次タグ付け統計情報 (21日〜翌月20日)</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>期間</TableCell>
                          <TableCell>作成数</TableCell>
                          <TableCell>削除数</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyTagStats.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              月次データが見つかりません
                            </TableCell>
                          </TableRow>
                        ) : (
                          monthlyTagStats.map((month, index) => (
                            <TableRow key={index}>
                              <TableCell>{month.period}</TableCell>
                              <TableCell>{month.created_count}</TableCell>
                              <TableCell>{month.deleted_count}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* 広告利用情報 */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>広告利用情報</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1">エリアトップ広告</Typography>
                          <Typography variant="h4" color="primary">
                            {adUsageStats.top_ad_clicks || 0}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">クリック回数</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1">インフィード広告</Typography>
                          <Typography variant="h4" color="primary">
                            {adUsageStats.inline_ad_clicks || 0}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">クリック回数</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: '#f8f9fa' }}>
                        <CardContent>
                          <Typography variant="subtitle1">当月広告利用料（確定）</Typography>
                          <Typography variant="h4" color="primary">
                            {(adUsageStats.current_month_amount || 0).toLocaleString()}円
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                            {adUsageStats.current_month_period || ''}
                          </Typography>
                          {/* 内訳表示 */}
                          <Box sx={{ borderTop: '1px dashed #ccc', pt: 1, fontSize: '0.8rem' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>基本料金（企業＋ユーザー{adUsageStats.current_tagged_count || 0}名）</span>
                              <span>{(adUsageStats.current_base_amount || 0).toLocaleString()}円</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>広告費（クリック課金）</span>
                              <span>{(adUsageStats.current_ad_amount || 0).toLocaleString()}円</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #ddd', mt: 0.5, pt: 0.5 }}>
                              <span>合計</span>
                              <span>{(adUsageStats.current_month_amount || 0).toLocaleString()}円</span>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: '#f8f9fa' }}>
                        <CardContent>
                          <Typography variant="subtitle1">次月広告利用料（予定）</Typography>
                          <Typography variant="h4" color="primary">
                            {(adUsageStats.next_month_amount || 0).toLocaleString()}円
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                            {adUsageStats.next_month_period || ''}
                          </Typography>
                          {/* 内訳表示 */}
                          <Box sx={{ borderTop: '1px dashed #ccc', pt: 1, fontSize: '0.8rem' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>基本料金（企業＋ユーザー{adUsageStats.next_tagged_count || 0}名）</span>
                              <span>{(adUsageStats.next_base_amount || 0).toLocaleString()}円</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>広告費（クリック課金）</span>
                              <span>{(adUsageStats.next_ad_amount || 0).toLocaleString()}円</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #ddd', mt: 0.5, pt: 0.5 }}>
                              <span>合計</span>
                              <span>{(adUsageStats.next_month_amount || 0).toLocaleString()}円</span>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose}>閉じる</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => downloadCsv(selectedCorporate?.id)}
            startIcon={<DownloadIcon />}
          >
            CSVダウンロード
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>
          企業ユーザー削除の確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {corporateToDelete && (
              <>
                企業ユーザー「{corporateToDelete.shop_name}」を削除しますか？<br />
                <strong style={{ color: 'red' }}>この企業ユーザーがタグ付けしたユーザーも一括で削除されます。</strong><br />
                この操作は元に戻せません。削除前にバックアップを取ることをお勧めします。
              </>
            )}
          </DialogContentText>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => corporateToDelete && downloadBackupCsv(corporateToDelete.id)}
              disabled={backupLoading}
              startIcon={backupLoading ? <CircularProgress size={20} /> : <BackupIcon />}
              sx={{ mr: 1 }}
            >
              バックアップCSV
            </Button>
            
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteCorporate}
              disabled={deleteInProgress || backupLoading}
              startIcon={deleteInProgress ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              削除する
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
        </DialogActions>
      </Dialog>

      {/* CSVアップロードダイアログ */}
      <Dialog open={csvUploadOpen} onClose={handleCloseCsvUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>企業ユーザーCSVアップロード</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              企業ユーザーとタグ付けユーザーの情報をCSVファイルからインポートします。
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              CSVフォーマット説明
            </Typography>
            <Typography variant="body2" component="div">
              <Box component="p" sx={{ mt: 1 }}>CSVファイルは以下のセクションを含みます：</Box>
              <ul>
                <li><strong>[CORPORATE_USER]</strong> - 企業ユーザー基本情報（必須）</li>
                <li><strong>[CORPORATE_CITIES]</strong> - 企業ユーザーの対象市区町村コード（任意）</li>
                <li><strong>[TAGGED_USERS]</strong> - タグ付けユーザー情報（任意）</li>
                <li><strong>[POSTER_PROFILES]</strong> - 投稿ユーザープロフィール情報（任意）</li>
              </ul>
              <Box component="p">
                企業ユーザーセクションには以下の項目を含めることができます：<br />
                id, username, email, display_name, shop_name, prefecture_code, phone_number, created_at, business_start_time, business_end_time, official_line, job_posting_url, avatar, cover_image, bio, location, website
              </Box>
              <Box component="p">
                タグ付けユーザーセクションには以下の項目を含めることができます：<br />
                id, username, email, display_name, tag_name, created_at, deleted_at, role, avatar, cover_image, bio, location, website
              </Box>
              <Box component="p">
                投稿ユーザープロフィールセクションには以下の項目を含めることができます：<br />
                user_id, username, shop_name, prefecture_code, city_code, stage_name, age, height, bust, cup_size, waist, hip
              </Box>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<DownloadIcon />}
            >
              CSVファイル選択
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileSelect}
              />
            </Button>
            <Typography variant="body2" sx={{ ml: 2 }}>
              {csvFile ? csvFile.name : 'ファイルが選択されていません'}
            </Typography>
          </Box>
          
          {csvUploadStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {csvUploadError}
            </Alert>
          )}
          
          {csvUploadStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              CSVファイルが正常にアップロードされました。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCsvUploadDialog}>キャンセル</Button>
          <Button 
            onClick={handleUploadCsv} 
            color="primary" 
            disabled={!csvFile || csvUploadStatus === 'loading'}
            startIcon={csvUploadStatus === 'loading' ? <CircularProgress size={20} /> : null}
          >
            アップロード
          </Button>
        </DialogActions>
      </Dialog>

      {/* 都道府県別料金設定ダイアログ */}
      <Dialog 
        open={pricingDialogOpen} 
        onClose={handleClosePricingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedPrefecture?.name || '都道府県'}の料金設定
        </DialogTitle>
        <DialogContent>
          {pricingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {selectedPrefecture?.name || ''}の企業ユーザーとタグ付けユーザーの料金設定を行います。
                設定変更は{effectiveDate}から適用されます。
              </DialogContentText>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    企業ユーザー料金設定
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="企業単価（月額・税込）"
                    name="corporate_price"
                    type="number"
                    value={prefecturePricing.corporate_price}
                    onChange={handlePricingChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">円</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="企業単価割引額"
                    name="corporate_discount"
                    type="number"
                    value={prefecturePricing.corporate_discount}
                    onChange={handlePricingChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">円</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
                    タグ付けユーザー料金設定
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ユーザー単価（月額・税込）"
                    name="user_price"
                    type="number"
                    value={prefecturePricing.user_price}
                    onChange={handlePricingChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">円</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ユーザー単価割引額"
                    name="user_discount"
                    type="number"
                    value={prefecturePricing.user_discount}
                    onChange={handlePricingChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">円</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    この設定は{selectedPrefecture?.name || ''}内の全ての企業ユーザーと
                    そのタグ付けユーザーに適用されます。
                    設定は{effectiveDate}から適用されます。
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePricingDialog} color="primary">
            キャンセル
          </Button>
          <Button 
            onClick={handleOpenConfirmDialog} 
            color="primary" 
            variant="contained"
            disabled={pricingLoading}
          >
            設定する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 料金設定確認ダイアログ */}
      <Dialog
        open={pricingConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>設定を確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            この設定は{selectedPrefecture?.name || ''}内の<strong>全ての企業ユーザーとタグ付けユーザー</strong>に適用されます。
            よろしいですか？
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              <strong>企業単価：</strong> {prefecturePricing.corporate_price.toLocaleString()}円（税込）
            </Typography>
            <Typography variant="body1">
              <strong>企業単価割引：</strong> {prefecturePricing.corporate_discount.toLocaleString()}円
            </Typography>
            <Typography variant="body1">
              <strong>ユーザー単価：</strong> {prefecturePricing.user_price.toLocaleString()}円（税込）
            </Typography>
            <Typography variant="body1">
              <strong>ユーザー単価割引：</strong> {prefecturePricing.user_discount.toLocaleString()}円
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>適用開始日：</strong> {effectiveDate}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            キャンセル
          </Button>
          <Button 
            onClick={updatePrefecturePricing} 
            color="primary" 
            variant="contained"
            disabled={pricingLoading}
          >
            {pricingLoading ? <CircularProgress size={24} /> : '確定する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorporateManagement; 